import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, ArrowLeft, Upload } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Button, Input, Checkbox } from '../components/ui';

interface Attribute {
  _id: string;
  name: string;
  displayName: string;
  type: 'select' | 'color' | 'text';
  description?: string;
}

interface AttributeValue {
  _id: string;
  attribute: string | Attribute;
  value: string;
  displayOrder: number;
  colorHex?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AttributeValues: React.FC = () => {
  const params = useParams<{ attributeId: string }>();
  const attributeId = params?.attributeId;
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Debug: Log the attributeId to help diagnose the issue
  useEffect(() => {
    if (!attributeId) {
      console.error('AttributeValues: attributeId is undefined. Params:', params);
    } else {
      console.log('AttributeValues: attributeId is', attributeId);
    }
  }, [attributeId, params]);
  const [attribute, setAttribute] = useState<Attribute | null>(null);
  const [values, setValues] = useState<AttributeValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingValue, setEditingValue] = useState<AttributeValue | null>(null);
  const [formData, setFormData] = useState({
    value: '',
    displayOrder: 0,
    colorHex: '#000000',
    isActive: true,
  });
  const [bulkValues, setBulkValues] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAttribute = useCallback(async () => {
    if (!attributeId) {
      console.error('Attribute ID is missing');
      navigate('/attributes');
      return;
    }
    try {
      const response = await api.get(`/admin/attributes/${attributeId}`);
      setAttribute(response.data.data);
    } catch (error) {
      console.error('Error fetching attribute:', error);
      navigate('/attributes');
    }
  }, [attributeId, navigate]);

  const fetchValues = useCallback(async () => {
    if (!attributeId) {
      console.error('Attribute ID is missing');
      return;
    }
    try {
      setLoading(true);
      const response = await api.get(`/admin/attribute-values/attribute/${attributeId}`);
      setValues(response.data.data);
    } catch (error) {
      console.error('Error fetching values:', error);
    } finally {
      setLoading(false);
    }
  }, [attributeId]);

  useEffect(() => {
    if (attributeId) {
      fetchAttribute();
      fetchValues();
    } else {
      // If attributeId is missing, redirect to attributes page
      console.warn('Attribute ID not found in route params, redirecting...');
      navigate('/attributes');
    }
  }, [attributeId, navigate, fetchAttribute, fetchValues]);

  const filteredValues = values.filter(value =>
    searchTerm === '' ||
    value.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ValueCard: React.FC<{ value: AttributeValue }> = ({ value }) => {
    return (
      <div className="card p-4 hover:shadow-card-hover transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            {attribute?.type === 'color' && value.colorHex && (
              <div
                className="w-10 h-10 rounded-lg border-2 border-gray-200 flex-shrink-0"
                style={{ backgroundColor: value.colorHex }}
              />
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900">{value.value}</h4>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`badge-${value.isActive ? 'green' : 'red'} text-xs`}>
                  {value.isActive ? t('badges.active', { ns: 'attributeValues' }) : t('badges.inactive', { ns: 'attributeValues' })}
                </span>
                <span className="text-xs text-gray-500">{t('badges.order', { ns: 'attributeValues' })}: {value.displayOrder}</span>
                {value.colorHex && (
                  <span className="text-xs text-gray-500 font-mono">{value.colorHex}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleEdit(value)}
              className="text-custom-primary hover:text-custom-primary-hover p-1"
            >
              <Edit2 size={16} />
            </button>
            <button
              onClick={() => handleDelete(value)}
              className="text-red-600 hover:text-red-700 p-1"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.value.trim()) return;

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        attribute: attributeId,
      };

      if (editingValue) {
        await api.put(`/admin/attribute-values/${editingValue._id}`, submitData);
      } else {
        await api.post('/admin/attribute-values', submitData);
      }

      setShowModal(false);
      setEditingValue(null);
      setFormData({
        value: '',
        displayOrder: values.length,
        colorHex: '#000000',
        isActive: true,
      });
      fetchValues();
    } catch (error: any) {
      console.error('Error saving value:', error);
      alert(error.response?.data?.message || t('messages.errorSaving', { ns: 'attributeValues' }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkValues.trim()) return;

    setSubmitting(true);
    try {
      const valuesArray = bulkValues
        .split('\n')
        .map(v => v.trim())
        .filter(v => v.length > 0);

      await api.post('/admin/attribute-values/bulk', {
        attributeId,
        values: valuesArray,
      });

      setShowBulkModal(false);
      setBulkValues('');
      fetchValues();
    } catch (error: any) {
      console.error('Error bulk creating values:', error);
      alert(error.response?.data?.message || t('messages.errorCreating', { ns: 'attributeValues' }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (value: AttributeValue) => {
    setEditingValue(value);
    setFormData({
      value: value.value,
      displayOrder: value.displayOrder,
      colorHex: value.colorHex || '#000000',
      isActive: value.isActive,
    });
    setShowModal(true);
  };

  const handleDelete = async (value: AttributeValue) => {
    if (window.confirm(t('messages.confirmDelete', { ns: 'attributeValues', name: value.value }))) {
      try {
        await api.delete(`/admin/attribute-values/${value._id}`);
        fetchValues();
      } catch (error: any) {
        console.error('Error deleting value:', error);
        alert(error.response?.data?.message || t('messages.errorDeleting', { ns: 'attributeValues' }));
      }
    }
  };

  const openCreateModal = () => {
    setEditingValue(null);
    setFormData({
      value: '',
      displayOrder: values.length,
      colorHex: '#000000',
      isActive: true,
    });
    setShowModal(true);
  };

  const openBulkModal = () => {
    setBulkValues('');
    setShowBulkModal(true);
  };

  if (!attribute) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <Button
          onClick={() => navigate('/attributes')}
          variant="outline"
          icon={ArrowLeft}
          size="sm"
          className="mb-4"
        >
          {t('backToAttributes', { ns: 'attributeValues' })}
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{attribute.displayName}</h1>
            <p className="text-gray-600 mt-2">
              {t('subtitle', { ns: 'attributeValues' })} <span className="font-medium">{attribute.type}</span>
            </p>
          </div>
          <div className="flex space-x-2">
            <Button onClick={openBulkModal} icon={Upload} variant="outline">
              {t('bulkAdd', { ns: 'attributeValues' })}
            </Button>
            <Button onClick={openCreateModal} icon={Plus} variant="primary">
              {t('addValue', { ns: 'attributeValues' })}
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-6">
        <div className="max-w-md">
          <Input
            type="text"
            placeholder={t('searchPlaceholder', { ns: 'attributeValues' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
            variant="search"
          />
        </div>
      </div>

      {/* Values Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-primary"></div>
          </div>
        ) : filteredValues.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchTerm ? t('messages.noValuesMatchingSearch', { ns: 'attributeValues' }) : t('messages.noValuesFound', { ns: 'attributeValues' })}
          </div>
        ) : (
          filteredValues.map((value, index) => (
            <ValueCard key={value._id || `value-${index}`} value={value} />
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingValue ? t('editValue', { ns: 'attributeValues' }) : t('addValue', { ns: 'attributeValues' })}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('fields.value', { ns: 'attributeValues' }) + ' *'}
                type="text"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder={t('fields.valuePlaceholder', { ns: 'attributeValues' })}
                required
              />

              {attribute.type === 'color' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fields.colorHexCode', { ns: 'attributeValues' })} *
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={formData.colorHex}
                      onChange={(e) => setFormData({ ...formData, colorHex: e.target.value })}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={formData.colorHex}
                      onChange={(e) => setFormData({ ...formData, colorHex: e.target.value })}
                      placeholder={t('fields.colorHexPlaceholder', { ns: 'attributeValues' })}
                      pattern="^#[0-9A-Fa-f]{6}$"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('fields.colorHexHelp', { ns: 'attributeValues' })}
                  </p>
                </div>
              )}

              <Input
                label={t('fields.displayOrder', { ns: 'attributeValues' })}
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                placeholder="0"
                min="0"
              />

              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                label={t('fields.active', { ns: 'attributeValues' })}
                variant="default"
              />

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  variant="outline"
                  disabled={submitting}
                >
                  {t('cancel', { ns: 'common' })}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.value.trim()}
                  variant="primary"
                  loading={submitting}
                >
                  {editingValue ? t('buttons.saveChanges', { ns: 'common' }) : t('buttons.add', { ns: 'common' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('bulkAddValues', { ns: 'attributeValues' })}
            </h3>

            <form onSubmit={handleBulkSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.valuesOnePerLine', { ns: 'attributeValues' })} *
                </label>
                <textarea
                  value={bulkValues}
                  onChange={(e) => setBulkValues(e.target.value)}
                  className="input-field font-mono"
                  rows={8}
                  placeholder={t('fields.bulkPlaceholder', { ns: 'attributeValues' })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('fields.bulkHelp', { ns: 'attributeValues' })}
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowBulkModal(false)}
                  variant="outline"
                  disabled={submitting}
                >
                  {t('cancel', { ns: 'common' })}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !bulkValues.trim()}
                  variant="primary"
                  loading={submitting}
                >
                  {t('buttons.createValues', { ns: 'attributeValues' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributeValues;
